import React, { useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import Popper from '@material-ui/core/Popper';
import Fade from '@material-ui/core/Fade';
import Icon from '@material-ui/core/Icon';
import ValidationEvent from 'components/ui/form/ValidationEvent';
import ValidationEventImpl from 'components/ui/form/ValidationEventImpl';
import PasswordStrengthBar from 'components/ui/form/PasswordStrengthBar';
import clsx from 'clsx';

let ValidatedTextField = (props: any) => {
  const VALID_DATE_DD_MM_YYYY = /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/;
  const VALID_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      arrowPopper: {
        backgroundColor: 'rgba(0, 0, 0, 0.54)',
        color: '#FFFFFF',
        zIndex: 1,
        '&[x-placement*="bottom"] .arrow': {
          width: 0,
          height: 0,
          borderLeft: '1em solid transparent',
          borderRight: '1em solid transparent',
          borderBottom: '1em solid rgba(0, 0, 0, 0.54)',
          marginTop: '-0.9em',
        
          '&:before': {
            borderWidth: '0 1em 1em 1em',
            borderColor: 'transparent transparent rgba(0, 0, 0, 0.54) transparent',
          }
        },

        '&[x-placement*="top"] .arrow': {
          bottom: 0,
          width: 0,
          height: 0,
          borderLeft: '1em solid transparent',
          borderRight: '1em solid transparent',
          borderTop: '1em solid rgba(0, 0, 0, 0.54)',
          marginBottom: '-0.9em',

          '&:before': {
            borderWidth: '1em 1em 0 1em',
            borderColor: 'rgba(0, 0, 0, 0.54) transparent transparent transparent',
          }
        },

        '&[x-placement*="right"] .arrow': {
          left: 0,
          width: 0, 
          height: 0, 
          borderTop: '1em solid transparent',
          borderBottom: '1em solid transparent',
          borderRight: '1em solid rgba(0, 0, 0, 0.54)',
          marginLeft: '-0.9em',

          '&:before': {
            borderWidth: '1em 1em 1em 0',
            borderColor: 'transparent rgba(0, 0, 0, 0.54) transparent transparent',
          }
        },

        '&[x-placement*="left"] .arrow': {
          
          right: 0,
          width: 0, 
          height: 0, 
          borderTop: '1em solid transparent',
          borderBottom: '1em solid transparent',
          borderLeft: '1em solid rgba(0, 0, 0, 0.54)',
          marginRight: '-0.9em',

          '&:before': {
            borderWidth: '1em 0 1em 1em',
            borderColor: 'transparent transparent transparent rgba(0, 0, 0, 0.54)',
          }
        }
      },      
      arrow: {
        position: 'absolute',
        fontSize: '7px',
        width: '3em',
        height: '3em',
      },   
      errorMessageContainer: {
        position: 'relative',
      },     
      buttonIcon: {
        marginRight: '0.33em',
      },
      textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '100%'
      },
      dense: {
        marginTop: '12px',
      },
      paper: {
        border: '1px solid',
        padding: theme.spacing(1),
        // backgroundColor: theme.palette.background.paper,
      },
      validField: {
      },
      invalidField: {
        backgroundColor: '#FFcccc'
      } 
    })
  );
  const classes = useStyles();
  
  let invalidIcon = <Icon color = 'secondary' className = { clsx(classes.buttonIcon,  'fa fa-exclamation-circle') } />;
  let validIcon = <Icon color = 'primary' className = { clsx(classes.buttonIcon,  'fa fa-check-circle') } />;
  
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [valueOnFocus, setValueOnFocus] = useState<string>();
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [textFieldEl, setTextFieldEl] = React.useState<null | HTMLElement>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLSpanElement | null>(null);
  
  const validate = (event: ValidationEvent<HTMLInputElement>) => {
    if(props.beforeValidate) {
      event.isValid = isValid;
      props.beforeValidate(event);
    }
    let newIsValid = true;
    let errorMessages: any = {};
    if(props.required) {
      if(!required(event)) {
        errorMessages.required = `This is a required field.`;
        newIsValid = false;
      }
    }

    if(props.minLength) {
      if(!minLength(event, props.minLength)) {
        errorMessages.minLength = `Must be at least ${props.minLength} characters.`;
        newIsValid = false;
      }
    }
    
    if(props.maxLength) {
      if(!maxLength(event, props.maxLength)) {
        errorMessages.maxLength = `Cannot be longer than ${props.maxLength} characters.`;
        newIsValid = false;
      }
    }    

    if(props.date) {
      if(!date(event)) {
        errorMessages.email = `Must be a valid date.`;
        newIsValid = false;
      }
    }

    if(props.email) {
      if(!email(event)) {
        errorMessages.email = `Must be a valid email address.`;
        newIsValid = false;
      }
    }

    setIsValid(newIsValid);
    setErrorMessages(errorMessages);
    if(props.afterValidate) {
      event.isValid = newIsValid;
      props.afterValidate(event);
    }
    return newIsValid;
  }

  const required = (event: ValidationEvent<HTMLInputElement>): boolean => {
    let isValid =
      event.target.value !== undefined &&
      event.target.value !== null &&
      event.target.value.trim().length > 0;
    return isValid;
  }

  const minLength = (event: ValidationEvent<HTMLInputElement>, len: number): boolean => {
    let isValid =
      event.target.value === undefined ||
      event.target.value === null ||
      event.target.value.trim().length === 0 ||
      event.target.value.trim().length >= len;
    return isValid;
  }

  const maxLength = (event: ValidationEvent<HTMLInputElement>, len: number): boolean => {
    let isValid =
      event.target.value === undefined ||
      event.target.value === null ||
      event.target.value.trim().length === 0 ||
      event.target.value.trim().length <= len;
    return isValid;
  }

  const date = (event: ValidationEvent<HTMLInputElement>): boolean => {
    let isValid =
      event.target.value === undefined ||
      event.target.value === null ||
      event.target.value.trim().length === 0 ||
      VALID_DATE_DD_MM_YYYY.test(event.target.value);
    return isValid;
  }

  const email = (event: ValidationEvent<HTMLInputElement>): boolean => {
    let isValid =
      event.target.value === undefined ||
      event.target.value === null ||
      event.target.value.trim().length === 0 ||
      VALID_EMAIL.test(event.target.value);
    return isValid;
  }

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setValueOnFocus(event.target.value)
  }

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    let validationEvent = new ValidationEventImpl<HTMLInputElement>(event.target);
    if(event.target.value.trim() !== '') {
      if(valueOnFocus !== event.target.value) {
        setIsDirty(true);
        setShowErrorMessage(!validate(validationEvent));
      } else {
        setIsDirty(false);
      }
    }
    if(props.onBlur) {
      props.onBlur(event);
    } 
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if(props.showPasswordStrength) {
      let validationEvent = new ValidationEventImpl<HTMLInputElement>(event.target);
      setShowErrorMessage(!validate(validationEvent));
    }
    if(props.onChange) {
      props.onChange(event);
    } 
  }
    
  let textFieldProps = Object.assign({}, props);
  delete textFieldProps.date;
  delete textFieldProps.email;
  delete textFieldProps.InputProps;
  delete textFieldProps.beforeValidate;
  delete textFieldProps.afterValidate;
  delete textFieldProps.minLength;
  delete textFieldProps.maxLength;
  delete textFieldProps.showPasswordStrength;
  
  return (
    <FormControl fullWidth={props.fullWidth}>
      <TextField 
        margin = "dense"
        variant = "outlined"
        { ...textFieldProps }
        className = { clsx(classes.textField, classes.dense, isValid ? classes.validField : classes.invalidField, props.className) }
        onChange = { onChange }
        onBlur = { onBlur }
        onFocus = { onFocus }
        ref = {el => { setTextFieldEl(el) }}
        InputProps={{
          endAdornment: (
            <div>   
              { props.InputProps ? props.InputProps.endAdornment : <span/> }
              <InputAdornment position="end" >
                { 
                  isDirty ?
                    isValid ? validIcon : invalidIcon
                  : <span/>                      
                }
              </InputAdornment>
            </div>
          ),
          ...props.InputProps,
        }} 
      >{props.children}</TextField>
      { isDirty && props.showPasswordStrength && 
        <PasswordStrengthBar value = { props.value } />
      }
      { showErrorMessage && 
        <span className = { classes.errorMessageContainer }>
          <Popper
            className = { classes.arrowPopper }
            open = { showErrorMessage }
            anchorEl = { textFieldEl }
            placement = 'right'
            modifiers={{
              preventOverflow: {
                enabled: true,
                boundariesElement: 'scrollParent',
              },
              arrow: {
                enabled: Boolean(arrowRef),
                element: arrowRef || {},
              },
            }}
            transition>
              {({ TransitionProps }) => (
                <Fade { ...TransitionProps } timeout={350}>
                  <div>
                    <span className = { clsx("arrow", classes.arrow) } ref = { setArrowRef } />
                    <div className={clsx(classes.paper)}>{ Object.values(errorMessages).join('') }</div>
                  </div>
                </Fade>
              )}
            
          </Popper>
          
        </span>
        
      }
  </FormControl>
  )
}

export default ValidatedTextField;